import { RoomTypeID } from '@kamernet/core/ApiClient/Kamernet';
import { useIntl } from '@kamernet/core/Intl';

import { ListingsContext } from '@kamernet/modules/Listing/context/ListingsContext';

export const useMetaTags = (): {
  metaTitle: string;
  metaDescription: string;
} => {
  const { listingsState } = ListingsContext.useContext();
  const { filters } = listingsState.filterResult;
  const { T } = useIntl();
  const { streetName, listingTypeIds } = filters;
  const cityName = filters.location?.cityName;
  const countryName = T('LBL_NETHERLANDS');
  const firstListingType = listingTypeIds?.[0];
  const isGenericSearch = listingTypeIds?.length !== 1;
  let metaTitle: string;
  let metaDescription: string;

  // street
  if (streetName && cityName) {
    if (isGenericSearch) {
      metaTitle = T(
        'listings_page.meta_tags.title.street.generic',
        streetName,
        cityName,
      );

      metaDescription = T(
        'listings_page.meta_tags.description.street.generic',
        streetName,
        cityName,
      );
    } else if (firstListingType === RoomTypeID.Room) {
      metaTitle = T(
        'listings_page.meta_tags.title.street.room',
        streetName,
        cityName,
      );

      metaDescription = T(
        'listings_page.meta_tags.description.street.room',
        streetName,
        cityName,
      );
    } else if (firstListingType === RoomTypeID.Apartment) {
      metaTitle = T(
        'listings_page.meta_tags.title.street.apartment',
        streetName,
        cityName,
      );

      metaDescription = T(
        'listings_page.meta_tags.description.street.apartment',
        streetName,
        cityName,
      );
    } else if (firstListingType === RoomTypeID.Studio) {
      metaTitle = T(
        'listings_page.meta_tags.title.street.studio',
        streetName,
        cityName,
      );

      metaDescription = T(
        'listings_page.meta_tags.description.street.studio',
        streetName,
        cityName,
      );
    } else if (firstListingType === RoomTypeID.Antikraak) {
      metaTitle = T(
        'listings_page.meta_tags.title.street.anti_squat',
        streetName,
        cityName,
      );

      metaDescription = T(
        'listings_page.meta_tags.description.street.anti_squat',
        streetName,
        cityName,
      );
    } else if (firstListingType === RoomTypeID.StudentResidence) {
      metaTitle = T(
        'listings_page.meta_tags.title.street.student_housing',
        streetName,
        cityName,
      );

      metaDescription = T(
        'listings_page.meta_tags.description.street.student_housing',
        streetName,
        cityName,
      );
    } else {
      metaTitle = T(
        'listings_page.meta_tags.title.street.generic',
        streetName,
        cityName,
      );

      metaDescription = T(
        'listings_page.meta_tags.description.street.generic',
        streetName,
        cityName,
      );
    }
  }
  // city
  else if (cityName) {
    if (isGenericSearch) {
      metaTitle = T('listings_page.meta_tags.title.city.generic', cityName);

      metaDescription = T(
        'listings_page.meta_tags.description.city.generic',
        cityName,
      );
    } else if (firstListingType === RoomTypeID.Room) {
      metaTitle = T('listings_page.meta_tags.title.city.room', cityName);

      metaDescription = T(
        'listings_page.meta_tags.description.city.room',
        cityName,
      );
    } else if (firstListingType === RoomTypeID.Apartment) {
      metaTitle = T('listings_page.meta_tags.title.city.apartment', cityName);

      metaDescription = T(
        'listings_page.meta_tags.description.city.apartment',
        cityName,
      );
    } else if (firstListingType === RoomTypeID.Studio) {
      metaTitle = T('listings_page.meta_tags.title.city.studio', cityName);

      metaDescription = T(
        'listings_page.meta_tags.description.city.studio',
        cityName,
      );
    } else if (firstListingType === RoomTypeID.Antikraak) {
      metaTitle = T('listings_page.meta_tags.title.city.anti_squat', cityName);

      metaDescription = T(
        'listings_page.meta_tags.description.city.anti_squat',
        cityName,
      );
    } else if (firstListingType === RoomTypeID.StudentResidence) {
      metaTitle = T(
        'listings_page.meta_tags.title.city.student_housing',
        cityName,
      );

      metaDescription = T(
        'listings_page.meta_tags.description.city.student_housing',
        cityName,
      );
    } else {
      metaTitle = T(
        'listings_page.meta_tags.title.city.generic',
        cityName,
        countryName,
      );

      metaDescription = T(
        'listings_page.meta_tags.description.city.generic',
        cityName,
        countryName,
      );
    }
  }
  // country
  else if (isGenericSearch) {
    metaTitle = T('listings_page.meta_tags.title.country.generic', countryName);

    metaDescription = T(
      'listings_page.meta_tags.description.country.generic',
      countryName,
    );
  } else if (firstListingType === RoomTypeID.Room) {
    metaTitle = T('listings_page.meta_tags.title.country.room', countryName);

    metaDescription = T(
      'listings_page.meta_tags.description.country.room',
      countryName,
    );
  } else if (firstListingType === RoomTypeID.Apartment) {
    metaTitle = T(
      'listings_page.meta_tags.title.country.apartment',
      countryName,
    );

    metaDescription = T(
      'listings_page.meta_tags.description.country.apartment',
      countryName,
    );
  } else if (firstListingType === RoomTypeID.Studio) {
    metaTitle = T('listings_page.meta_tags.title.country.studio', countryName);

    metaDescription = T(
      'listings_page.meta_tags.description.country.studio',
      countryName,
    );
  } else if (firstListingType === RoomTypeID.Antikraak) {
    metaTitle = T(
      'listings_page.meta_tags.title.country.anti_squat',
      countryName,
    );

    metaDescription = T(
      'listings_page.meta_tags.description.country.anti_squat',
      countryName,
    );
  } else if (firstListingType === RoomTypeID.StudentResidence) {
    metaTitle = T(
      'listings_page.meta_tags.title.country.student_housing',
      countryName,
    );

    metaDescription = T(
      'listings_page.meta_tags.description.country.student_housing',
      countryName,
    );
  } else {
    metaTitle = T('listings_page.meta_tags.title.country.generic', countryName);

    metaDescription = T(
      'listings_page.meta_tags.description.country.generic',
      countryName,
    );
  }

  return { metaTitle, metaDescription };
};
